import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { ContactForm } from '../components/ContactForm'
import { TestimonialsSlider } from '../components/TestimonialsSlider'
import { useCMSSide, useCMSCollection } from '../hooks/useCMS'

export function ForloebPage() {
  const { side: cmsSide } = useCMSSide('forloeb')
  const { data: cmsForloeb } = useCMSCollection('forloeb')

  useSEO({
    title: cmsSide?.seo_titel || 'Forløb - BoSteendahl.dk',
    description: cmsSide?.seo_beskrivelse || 'Hos Bo Steendahl tilbydes behandlingsforløb for at arbejde i dybden med årsagerne frem for kun at lindre symptomer. Læs om processen og forundersøgelsen.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/20241001-091341632-ios.png',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Behandlingsforløb
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'Forløb — ikke enkeltbehandlinger'}
          </h1>
          <p className="mt-4 text-xl sm:text-2xl font-serif text-[#1b3a4b]/90 max-w-3xl mx-auto leading-relaxed">
            {cmsSide?.hero_underoverskrift || 'Jeg tilbyder behandlingsforløb og kun enkeltbehandlinger som opfølgning på forløb.'}
          </p>
        </div>
      </section>

      {/* Rationale & Depth */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 relative z-20">
        <div className="bg-[#172e3b] text-white rounded-2xl p-8 sm:p-10 shadow-xl border border-white/10 space-y-4">
          <div className="flex items-center gap-2 text-[#7c875a] text-xs font-semibold uppercase tracking-wider">
            <i className="fa-solid fa-arrow-down-long" aria-hidden="true" />
            <span>Hvorfor kun forløb?</span>
          </div>
          <h2 className="font-serif text-[21px] sm:text-3xl font-bold text-white">
            Arbejd i dybden med de underliggende årsager
          </h2>
          <p className="text-sm sm:text-base text-[#FAF8F5]/90 leading-relaxed">
            Årsagen til dette er at jeg ønsker at hjælpe dig til at arbejde i dybden, og finde ind til de underliggende årsager der har forårsaget det symptomer, der mærkes som f.eks. følelsesmæssige blokeringer, fordøjelsesproblemer, søvnbesvær, selvværdsproblematikker, adfærdsproblemer og grænsesætning.
          </p>
          <p className="text-sm sm:text-base text-[#FAF8F5]/90 leading-relaxed">
            Behandling hos mig er for dig, der ønsker hjælp til at bearbejde mentalt og følelsesmæssig stress, der opleves som ubalance, tankemylder, og smerter i kroppen. Det er for dig som ønsker en helhedsorienteret tilgang i arbejdet med de oplevelser du har med i bagagen, som holder dig tilbage fra at leve det liv du ønsker dig at leve.
          </p>
        </div>
      </section>

      {/* Forløbets 4 Faser */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-10">
              {/* Fase 1 */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <div>
              <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">Trin 1</span>
              <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">Før første session (Spørgeskema)</h3>
            </div>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Når du booker tid til første session, modtager du en mail med et link til et spørgeskema som du bedes udfylde før du møder op første gang.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Spørgeskemaet er en problemspecifik og individualiseret målemetode, hvor du selv vurdere de to vigtigste symptomer forbundet med et specifikt problem, og en aktivitet i dagligdagen, der er påvirket af disse symptomer. Dine svar bruges til at sætte retning for behandlingen og giver mulighed for at evaluere effekten.
            </p>
          </div>

          {/* Fase 2 */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <div>
              <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">Trin 2</span>
              <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">FORUNDERSØGELSE</h3>
            </div>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Ethvert forløb starter med en grundig forundersøgelse, hvor vi gennemgår dine udfordringer, og de oplevelser som har ført dig frem til hvor du er i dag. Under forundersøgelsen kommer vi ind på hvordan du hidtil har bearbejde disse oplevelser, og hvilken effekt du har oplevet af denne tilgang.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Hvis du tidligere har modtaget professionel hjælp, vil vi også tale om hvordan du har oplevet den måde at arbejde med dig selv på, og vi vil yderligere komme til at tale om din arbejds- og familiemæssige baggrund og nærmeste relationer.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Forundersøgelsen er delt mellem samtale og fysisk undersøgelse/behandling på briksen. Til sidst i konsultationen får du et proffesionelt tilbud om et behandlingsforløb, som er tilpasse dig og dine udfordringer, med en plan for hvordan jeg bedst muligt kan hjælpe dig videre mod det liv du ønsker dig.
            </p>
            <p className="text-xs text-[#64748B] italic">
              Alle oplysninger bliver behandlet fortroligt, og brugt til at planlægge den videre behandling.
            </p>
          </div>

          {/* Fase 3 */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <div>
              <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">Trin 3</span>
              <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">Under behandlingsforløbet</h3>
            </div>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Forløbet tilpasses løbende. Vi kombinerer kropsterapiens fysiske forløsning på briksen med coaching og hypnoterapi, alt efter hvad din krop og dit sind er parat til at møde.
            </p>
            <blockquote className="border-l-4 border-[#7c875a] bg-[#FAF8F5] p-5 rounded-r-lg text-sm sm:text-base italic text-[#334155] leading-relaxed">
              Tryghed er det vigtigste for en god behandling, som altid tilpasses til der hvor klienten er, og er klar til at modtage. Processen kan og skal ikke forceres. På samme måde som man ikke kan få en plante til at vokse hurtigere ved at trække i den, kan kroppen ikke slippe en spænding før den er klar til det.
            </blockquote>
          </div>

          {/* Fase 4 */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <div>
              <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">Trin 4</span>
              <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">Opfølgning og integration</h3>
            </div>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Behandlingen afsluttes evt. med en kort samtale, hvor du kan stille spørgsmål, og jeg komme med input til, hvordan du selv kan arbejde videre derhjemme i dagligdagen, enten gennem øvelser eller ting du kan være opmærksom på og bevidst om i dit møde med dine omgivelser.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Efter et gennemført forløb kan enkeltbehandlinger tilbydes som vedligeholdelse eller opfølgning efter behov.
            </p>
          </div>

              {/* Vilkår for forløb */}
              <div className="bg-[#FAF8F5] rounded-2xl p-6 sm:p-8 border border-[#E5E0D8] text-xs sm:text-sm text-[#64748B] space-y-2">
                <h4 className="font-serif font-bold text-base text-[#1b3a4b]">
                  Betingelser for forløb (Handelsbetingelser):
                </h4>
                <p>
                  Betaling for forløb skal ske inden forløbets begyndelse medmindre andet er aftalt.
                </p>
                <p>
                  Forløb sælges som en samlet pakke - det er ikke muligt at få refunderet betaling for forløb.
                </p>
                <p>
                  Aftalte sessioner i forløbet skal aflyses i henhold til gældende afbudspolitik (senest kl. 8 dagen før aftalt tid).
                </p>
              </div>
            </div>

            {/* Aside Contact Form */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 space-y-6">
              <ContactForm />
              <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] text-xs text-[#475569] space-y-2.5 shadow-sm">
                <div className="font-semibold text-[#1b3a4b] text-sm">Direkte kontakt</div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#7c875a]" aria-hidden="true" />
                  <span>Vendersgade 37, 7000 Fredericia</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
                  <a href="tel:53128213" className="hover:text-[#1b3a4b] font-medium">Tlf: 53 12 82 13</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Klientudtalelser */}
      <TestimonialsSlider />
    </div>
  )
}
